import { normalizeQuantityToStep } from './tg-commerce-inputs.js';

const STORAGE_KEY_PREFIX = 'tgRequestList:v1:';
const REQUEST_LIST_EVENT = 'tg:request-list:updated';
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export class RequestListStore {
  constructor(customerId, storage) {
    this.customerId = String(customerId || '');
    try {
      this.storage = storage || globalThis.localStorage;
    } catch {
      this.storage = null;
    }
    this.key = `${STORAGE_KEY_PREFIX}${this.customerId}`;
  }

  getState() {
    if (!this.customerId) return { items: [], error: null };

    try {
      const rawValue = this.storage.getItem(this.key);
      if (!rawValue) return { items: [], error: null };

      const parsedValue = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) throw new Error('Invalid request list');

      return { items: parsedValue.map((item) => this.#normalizeLine(item)).filter(Boolean), error: null };
    } catch (error) {
      return { items: [], error };
    }
  }

  getItems() {
    return this.getState().items;
  }

  add(item, quantity) {
    return this.addMany([{ item, quantity }])[0];
  }

  addMany(entries) {
    if (!Array.isArray(entries)) throw new Error('Invalid request items');
    if (entries.length === 0) return [];

    const normalizedEntries = entries.map((entry) => {
      if (!entry?.item) throw new Error('Invalid request item');
      const normalizedItem = this.#normalizeLine({ ...entry.item, quantity: entry.quantity });
      if (!normalizedItem) throw new Error('Invalid request item');
      return normalizedItem;
    });

    const state = this.getState();
    if (state.error) throw new Error('Request list storage is unavailable', { cause: state.error });
    const items = state.items;
    const addedItems = normalizedEntries.map((normalizedItem) => {
      const existingItem = items.find(
        (storedItem) =>
          storedItem.variantId === normalizedItem.variantId &&
          storedItem.requestedDate === normalizedItem.requestedDate
      );
      if (existingItem) {
        existingItem.quantity = this.#normalizeQuantity(
          existingItem.quantity + normalizedItem.quantity,
          existingItem
        );
        return existingItem;
      }

      items.push(normalizedItem);
      return normalizedItem;
    });

    this.#save(items);
    return addedItems;
  }

  setQuantity(variantId, requestedDate, quantity) {
    const items = this.getItems();
    const item = items.find(
      (storedItem) => storedItem.variantId === String(variantId) && storedItem.requestedDate === requestedDate
    );
    if (!item) throw new Error('Request item not found');

    item.quantity = this.#normalizeQuantity(quantity, item);
    this.#save(items);
    return item;
  }

  setDate(variantId, currentDate, requestedDate) {
    if (!DATE_PATTERN.test(requestedDate)) throw new Error('Invalid requested date');

    const items = this.getItems();
    const item = items.find(
      (storedItem) => storedItem.variantId === String(variantId) && storedItem.requestedDate === currentDate
    );
    if (!item) throw new Error('Request item not found');

    const isOccupied = items.some(
      (storedItem) =>
        storedItem !== item &&
        storedItem.variantId === item.variantId &&
        storedItem.requestedDate === requestedDate
    );
    if (isOccupied) throw new Error('Requested date already used');

    item.requestedDate = requestedDate;
    this.#save(items);
    return item;
  }

  duplicate(variantId, requestedDate) {
    const items = this.getItems();
    const sourceItem = items.find(
      (storedItem) => storedItem.variantId === String(variantId) && storedItem.requestedDate === requestedDate
    );
    if (!sourceItem) throw new Error('Request item not found');

    const nextDate = new Date(`${requestedDate}T00:00:00`);
    do {
      nextDate.setDate(nextDate.getDate() + 1);
    } while (
      items.some(
        (storedItem) =>
          storedItem.variantId === sourceItem.variantId &&
          storedItem.requestedDate === this.#formatDate(nextDate)
      )
    );

    const duplicatedItem = { ...sourceItem, requestedDate: this.#formatDate(nextDate) };
    items.push(duplicatedItem);
    this.#save(items);
    return duplicatedItem;
  }

  remove(variantId, requestedDate) {
    const items = this.getItems();
    const remainingItems = items.filter(
      (storedItem) =>
        storedItem.variantId !== String(variantId) || storedItem.requestedDate !== requestedDate
    );
    if (remainingItems.length === items.length) return false;

    this.#save(remainingItems);
    return true;
  }

  clear() {
    if (!this.customerId) return;

    try {
      this.storage.removeItem(this.key);
      this.#notify();
    } catch (error) {
      throw new Error('Request list storage is unavailable', { cause: error });
    }
  }

  count() {
    return this.getItems().length;
  }

  serializeItems() {
    return this.getItems().map((item, index) => ({
      name: `contact[item_${index + 1}]`,
      value: [
        `Product: ${item.productTitle}`,
        `SKU: ${item.sku || '-'}`,
        `Quantity: ${item.quantity}`,
        `Requested date: ${item.requestedDate}`,
        item.unitPrice ? `Unit price: ${item.unitPrice}` : null,
      ]
        .filter(Boolean)
        .join(' | '),
    }));
  }

  #normalizeLine(item) {
    if (!item || !item.variantId || !item.productId || !item.requestedDate || !DATE_PATTERN.test(item.requestedDate)) {
      return null;
    }

    const quantityStep = Math.max(1, Number(item.quantityStep) || 1);
    const quantityMinimum = Math.ceil(Math.max(1, Number(item.quantityMin) || 1) / quantityStep) * quantityStep;
    const normalizedItem = {
      productId: String(item.productId),
      variantId: String(item.variantId),
      productTitle: String(item.productTitle || ''),
      variantTitle: String(item.variantTitle || ''),
      sku: String(item.sku || ''),
      productUrl: String(item.productUrl || ''),
      imageUrl: String(item.imageUrl || ''),
      imageAlt: String(item.imageAlt || item.productTitle || ''),
      quantityMin: quantityMinimum,
      quantityMax: Number(item.quantityMax) > 0 ? Number(item.quantityMax) : null,
      quantityStep,
      requestedDate: item.requestedDate,
      quantity: 0,
    };

    if (item.unitPrice) normalizedItem.unitPrice = String(item.unitPrice);
    if (item.currency) normalizedItem.currency = String(item.currency);
    if (item.packaging) normalizedItem.packaging = String(item.packaging);

    normalizedItem.quantity = this.#normalizeQuantity(item.quantity, normalizedItem);
    return normalizedItem;
  }

  #normalizeQuantity(quantity, item) {
    return normalizeQuantityToStep(quantity, {
      min: item.quantityMin,
      max: item.quantityMax,
      step: item.quantityStep,
    }) ?? item.quantityMin;
  }

  #save(items) {
    if (!this.customerId) throw new Error('Sign in to use the request list');

    try {
      this.storage.setItem(this.key, JSON.stringify(items));
      this.#notify();
    } catch (error) {
      throw new Error('Request list storage is unavailable', { cause: error });
    }
  }

  #notify() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(REQUEST_LIST_EVENT, { detail: { customerId: this.customerId } }));
    }
  }

  #formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

/**
 * @param {HTMLElement} element
 * @param {Document} [documentRoot]
 */
export const getRequestCustomerId = (element, documentRoot = document) => {
  const customerId = element.dataset.customerId || element.closest('[data-customer-id]')?.getAttribute('data-customer-id');
  if (customerId) return customerId;

  return (
    [...documentRoot.querySelectorAll('[data-customer-id]')]
      .find((candidate) => candidate.getAttribute('data-customer-id'))
      ?.getAttribute('data-customer-id') || ''
  );
};

const getStore = (element) => {
  const customerId = getRequestCustomerId(element);
  if (!customerId) return null;
  return new RequestListStore(customerId);
};

const showStatus = (element, message, isError = false) => {
  const surface = element.closest('[data-request-surface]') || (element.matches('[data-request-surface]') ? element : null);
  const status = surface?.querySelector('[data-request-status]') || element.querySelector?.('[data-request-status]');
  if (!status) return;
  status.textContent = message;
  status.classList.toggle('tg-request-list__status--error', isError);
};

export const getRequestItem = (source, row = source.closest?.('tr'), quantity) => ({
  productId: source.dataset.productId,
  variantId: source.dataset.variantId,
  productTitle: source.dataset.productTitle,
  variantTitle: source.dataset.variantTitle,
  sku: source.dataset.sku,
  productUrl: source.dataset.productUrl,
  imageUrl: source.dataset.imageUrl,
  imageAlt: source.dataset.imageAlt,
  quantityMin: source.dataset.quantityMin,
  quantityMax: source.dataset.quantityMax,
  quantityStep: source.dataset.quantityStep,
  requestedDate: source.dataset.requestedDate,
  unitPrice: source.dataset.unitPrice,
  currency: source.dataset.currency,
  packaging: source.dataset.packaging,
  quantity: quantity ?? row?.querySelector('.tg-variant-table__request-quantity input')?.value ?? source.dataset.quantityMin,
});

const bindRequestButtons = () => {
  document.querySelectorAll('[data-request-button]').forEach((button) => {
    if (button.dataset.requestBound) return;
    button.dataset.requestBound = 'true';
    button.addEventListener('click', async () => {
      const store = getStore(button);
      if (!store) {
        window.location.href = button.dataset.loginUrl;
        return;
      }

      const row = button.closest('tr');
      const quantityInput = row?.querySelector('.tg-variant-table__request-quantity input');
      button.dataset.loading = 'true';
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      try {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        store.add(getRequestItem(button, row), quantityInput?.value);
        showStatus(button, button.dataset.successLabel || 'Added to request list');
      } catch (error) {
        showStatus(button, error.message, true);
      } finally {
        delete button.dataset.loading;
        button.removeAttribute('aria-busy');
        button.disabled = Number(quantityInput?.value) <= 0;
      }
    });
  });
};

const updateBadges = () => {
  document.querySelectorAll('[data-request-count]').forEach((badge) => {
    const store = getStore(badge);
    const count = store?.count() || 0;
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.hidden = count === 0;
  });
};

const renderRequestList = (root) => {
  const store = getStore(root);
  const itemsContainer = root.querySelector('[data-request-items]');
  const table = root.querySelector('[data-request-table]');
  const emptyState = root.querySelector('[data-request-empty]');
  const form = root.querySelector('[data-request-form], .tg-request-list__form');
  if (!store || !itemsContainer || !table || !emptyState) return;

  const state = store.getState();
  if (state.error) {
    showStatus(root, root.dataset.storageError || 'Request list storage is unavailable', true);
  }

  itemsContainer.replaceChildren();
  state.items.forEach((item) => itemsContainer.append(createRequestRow(item, root, store)));
  emptyState.hidden = state.items.length > 0;
  table.hidden = state.items.length === 0;
  itemsContainer.hidden = state.items.length === 0;
  if (form) form.querySelector('[data-request-submit]').disabled = state.items.length === 0;
};

const createRequestRow = (item, root, store) => {
  const row = document.createElement('article');
  row.className = 'tg-request-list__item';
  row.dataset.variantId = item.variantId;
  row.dataset.requestedDate = item.requestedDate;

  const product = document.createElement('div');
  product.className = 'tg-request-list__product';
  if (item.imageUrl) {
    const image = document.createElement('img');
    image.className = 'tg-request-list__product-image';
    image.src = item.imageUrl;
    image.alt = item.imageAlt;
    image.loading = 'lazy';
    product.append(image);
  }
  const productCopy = document.createElement('div');
  productCopy.className = 'tg-request-list__product-copy';
  const productLink = document.createElement('a');
  productLink.href = item.productUrl;
  productLink.textContent = item.productTitle;
  const sku = document.createElement('span');
  sku.textContent = item.sku || '-';
  productCopy.append(productLink, sku);
  product.append(productCopy);

  const date = document.createElement('input');
  date.type = 'date';
  date.className = 'tg-request-list__date';
  date.value = item.requestedDate;
  date.min = new Date().toISOString().slice(0, 10);
  date.setAttribute('aria-label', root.dataset.dateLabel || 'Requested date');
  date.addEventListener('change', () => {
    try {
      store.setDate(item.variantId, row.dataset.requestedDate, date.value);
      renderRequestList(root);
    } catch (error) {
      date.value = row.dataset.requestedDate;
      showStatus(root, root.dataset.dateConflict || error.message, true);
      row.classList.add('tg-request-list__item--conflict');
      window.setTimeout(() => row.classList.remove('tg-request-list__item--conflict'), 1500);
    }
  });

  const quantity = document.createElement('input');
  quantity.type = 'number';
  quantity.className = 'tg-request-list__quantity';
  quantity.value = item.quantity;
  quantity.min = item.quantityMin;
  quantity.step = item.quantityStep;
  if (item.quantityMax) quantity.max = item.quantityMax;
  quantity.setAttribute('aria-label', root.dataset.quantityLabel || 'Quantity');
  quantity.addEventListener('change', () => {
    try {
      store.setQuantity(item.variantId, row.dataset.requestedDate, quantity.value);
      renderRequestList(root);
    } catch (error) {
      showStatus(root, error.message, true);
    }
  });

  const actions = document.createElement('div');
  actions.className = 'tg-request-list__actions';
  const duplicate = createActionButton(root.dataset.duplicateLabel || 'Duplicate', root.dataset.copyIconUrl);
  duplicate.addEventListener('click', () => {
    try {
      store.duplicate(item.variantId, row.dataset.requestedDate);
      renderRequestList(root);
    } catch (error) {
      showStatus(root, error.message, true);
    }
  });
  const remove = createActionButton(root.dataset.removeLabel || 'Remove', root.dataset.deleteIconUrl);
  remove.classList.add('tg-request-list__remove');
  remove.addEventListener('click', () => {
    try {
      store.remove(item.variantId, row.dataset.requestedDate);
      renderRequestList(root);
    } catch (error) {
      showStatus(root, error.message, true);
    }
  });
  actions.append(duplicate);

  row.append(remove, product, date, quantity, actions);
  return row;
};

const createActionButton = (label, iconUrl) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button-unstyled';
  button.setAttribute('aria-label', label);
  const icon = document.createElement('span');
  icon.className = 'svg-wrapper';
  if (iconUrl) {
    icon.style.setProperty('--tg-request-icon-url', `url("${iconUrl}")`);
    const image = document.createElement('img');
    image.src = iconUrl;
    image.alt = '';
    icon.append(image);
  }
  button.append(icon);
  return button;
};

const bindRequestForm = (root) => {
  const form = root.querySelector('[data-request-form], .tg-request-list__form');
  if (!form || form.dataset.requestBound) return;
  form.dataset.requestBound = 'true';
  form.addEventListener('submit', () => {
    const store = getStore(root);
    if (!store || store.getItems().length === 0) return;

    const hiddenFields = form.querySelector('[data-request-hidden-fields]') || form;
    hiddenFields.querySelectorAll('[data-generated-request-field]').forEach((field) => field.remove());
    const fields = store.serializeItems();
    fields.forEach(({ name, value }) => {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.value = value;
      field.dataset.generatedRequestField = 'true';
      hiddenFields.append(field);
    });
  });
};

const boot = () => {
  bindRequestButtons();
  document.querySelectorAll('[data-request-list-root]').forEach((root) => {
    renderRequestList(root);
    bindRequestForm(root);
    const successMarker = root.querySelector('[data-request-submitted-successfully]');
    if (successMarker && root.dataset.successCleared !== 'true') {
      root.dataset.successCleared = 'true';
      getStore(root)?.clear();
    }
  });
  updateBadges();
};

if (typeof window !== 'undefined') {
  window.addEventListener(REQUEST_LIST_EVENT, boot);
  window.addEventListener('storage', (event) => {
    if (event.key?.startsWith(STORAGE_KEY_PREFIX)) boot();
  });
  window.addEventListener('pageshow', boot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
